<template>
  <v-card
    class="ma-3"
    v-if="AuthenticationMixin_LoggedIn && AuthenticationMixin_HasRights(['gebruiker','admin'])">
    <v-row class="justify-space-between">
      <v-col cols="9" md="6" lg="4">
        <v-text-field
          class="pa-2"
          clearable
          v-model="search"
          v-on:input="callDebounceSearch"
          append-icon="mdi-magnify"
          label="Zoek exports op naam of gebruiker"
        ></v-text-field>
      </v-col>
      <v-col cols="3" md="6" lg="8">
        <v-btn
          class="ma-5 float-right"
          color="primary"
          @click="refreshData"
        >
          <v-icon>mdi-refresh</v-icon>
          Vernieuwen
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      item-key="idExport"
      dense
      :headers="headers"
      :items="items"
      :search="search"
      :options.sync="options"
      :server-items-length="totalItems"
      :loading="loading"
      :footer-props="{
        itemsPerPageText: 'Exports per pagina',
        'items-per-page-options': [25, 50, 100]
      }"
      class="elevation-1"
      @click:row="openExportDetail"
      group-by="exportGroepHash"
    >
      <template v-slot:group.header="{ group, headers, toggle, isOpen, items }">
        <td :colspan="headers.length - 1">
          <v-btn @click="toggle" x-small icon>
            <v-icon>{{ isOpen ? 'mdi-minus' : 'mdi-plus' }}</v-icon>
          </v-btn>
          <strong>{{ items[0].exportGroepNaam }}</strong> ({{ items.length }})
        </td>
        <td class="text-center">
          <v-btn
            class="downloadGroupExport mr-10"
            icon
            :loading="downloadingGroups[items[0].exportGroepHash]"
            :disabled="downloadingGroups[items[0].exportGroepHash] || !hasDownloadableExports(items)"
            @click.stop="downloadGroupExport(items)"
            title="Download alle exports in deze groep als ZIP">
            <v-icon>mdi-folder-zip-outline</v-icon>
          </v-btn>
        </td>
      </template>

      <template v-slot:[`item.exportDatum`]="{ item }">
        {{ FormatMixin_FormatDateTime(item.exportDatum, formatMixin.date.database, formatMixin.date.dateTime) }}
      </template>

      <template v-slot:[`item.type`]="{ item }">
        {{ item.type ? item.type.charAt(0).toUpperCase() + item.type.slice(1) : '' }}
      </template>

      <template v-slot:[`item.exportStatusText`]="{ item }">
        <v-chip
          small
          :color="getStatusColor(item.exportStatusText)"
          dark
        >
          <span style="width: 100px; text-align: center;">{{ item.exportStatusText }}</span>
        </v-chip>
      </template>

      <template v-slot:[`item.acties`]="{ item }">
        <v-btn
          :disabled="!item.downloadUrl"
          class="downloadExport mr-2"
          icon
          @click="downloadExport(item)">
          <v-icon>mdi-download</v-icon>
        </v-btn>
        <v-btn
          class="deleteItem"
          icon
          @click="deleteItem(item)">
          <v-icon>mdi-delete</v-icon>
        </v-btn>
      </template>
    </v-data-table>

    <!-- DELETE DIALOG -->
    <v-dialog
        v-model="deletedialog"
        max-width="500">
        <v-card>
          <v-card-title class="text-h5">
            Export verwijderen
          </v-card-title>
          <v-card-text v-if="selectedItem">
            Ben je zeker dat je export '<strong>{{ selectedItem.exportNaam }}</strong>' wilt verwijderen?
          </v-card-text>
          <v-card-actions>
            <v-btn
              color="primary darken-1"
              @click="deletedialog = false">
              Nee
            </v-btn>
            <v-spacer></v-spacer>
            <v-btn
              color="red darken-1"
              text
              :loading="deleteLoading"
              :disabled="deleteLoading"
              @click="DeleteExport">
              Ja, verwijderen
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  </v-card>
  <v-card class="ma-3" v-else>
    <h2 class="primary--text pa-3">Log in om de export lijst te kunnen bekijken.</h2>
  </v-card>
</template>

<script>
import _ from 'lodash'
import { downloadExportGroupAsZip } from '../utils/zipDownloadUtil'
import templateService from '../services/templateService'
import commonMixin from '../mixins/commonMixin'
import errorMixin from '../mixins/errorMixin'
import authenticationMixin from '../mixins/authenticationMixin'
import formatMixin from '../mixins/formatMixin'

export default {
  mixins: [
    commonMixin,
    errorMixin,
    authenticationMixin,
    formatMixin
  ],
  name: 'ExportOverzichtList',
  data () {
    return {
      deletedialog: false,
      deleteLoading: false,
      action: false,
      selectedItem: null,
      totalItems: 0,
      items: [],
      search: null,
      loading: true,
      options: {},
      downloadingGroups: {},
      headers: [
        {
          text: 'Export Naam',
          align: 'start',
          sortable: false,
          value: 'exportNaam'
        },
        {
          text: 'Gebruiker',
          align: 'start',
          sortable: false,
          value: 'userNaam'
        },
        {
          text: 'Export Datum',
          align: 'start',
          value: 'exportDatum'
        },
        {
          text: 'Type',
          align: 'start',
          value: 'type'
        },
        {
          text: 'Format',
          align: 'start',
          value: 'format'
        },
        {
          text: 'Status',
          align: 'start',
          sortable: false,
          value: 'exportStatusText'
        },
        {
          text: '',
          sortable: false,
          align: 'center',
          value: 'acties'
        }
      ]
    }
  },
  watch: {
    options: {
      handler () {
        this.getDataFromApi()
      },
      deep: true
    },
    deletedialog () {
      if (!this.deletedialog) {
        this.action = false
      }
    }
  },
  mounted () {
    templateService.Init(this)

    const urlParams = new URLSearchParams(window.location.search)
    const searchTerm = urlParams.get('searchTerm')
    if (searchTerm) {
      this.search = searchTerm
    }
  },
  methods: {
    async GetExportsPaged (sortBy, sortType, search, page, pageSize) {
      const response = await templateService.GetExportsPaged(sortBy, sortType, search, page, pageSize)
      if (response && response.data) {
        const data = response.data
        this.items = data.items
        this.totalItems = data.totalItems
      }
    },
    async getDataFromApi () {
      this.loading = true
      await this.GetExportsPaged(this.options.sortBy[0], this.options.sortDesc[0] ? 'desc' : 'asc', this.search, this.options.page, this.options.itemsPerPage).then(data => {
        this.loading = false
      })
    },
    getStatusColor (status) {
      switch (status) {
        case 'Export klaar':
        case 'Completed':
          return '#2e7a2e'
        case 'In verwerking':
        case 'Processing':
          return '#356191'
        case 'Export mislukt':
        case 'Failed':
          return '#d32f2f'
        case 'Wachten':
        case 'Pending':
          return 'darkgrey'
        default:
          return 'grey'
      }
    },
    callDebounceSearch: _.debounce(function () {
      this.debounceSearch()
    }, 500),
    debounceSearch () {
      this.getDataFromApi()
    },
    refreshData () {
      this.getDataFromApi()
    },
    deleteItem (item) {
      this.selectedItem = item
      this.action = true
      this.deletedialog = true
    },
    getFileExtension (format) {
      if (!format) return '.docx'
      const formatLower = format.toLowerCase()
      if (formatLower === 'excel') return '.xlsx'
      if (formatLower === 'word') return '.docx'
      return '.docx'
    },
    getFilenameWithExtension (item) {
      const extension = this.getFileExtension(item.format)
      const basename = item.exportNaam || 'export'
      // Check if filename already has the correct extension
      if (basename.endsWith(extension)) {
        return basename
      }
      // Remove any existing extension and add the correct one
      const nameWithoutExt = basename.replace(/\.[^/.]+$/, '')
      return `${nameWithoutExt}${extension}`
    },
    async downloadExport (item) {
      this.action = true
      if (item.downloadUrl) {
        try {
          const response = await fetch(item.downloadUrl)
          const blob = await response.blob()
          const url = window.URL.createObjectURL(blob)
          const link = document.createElement('a')
          link.href = url
          link.download = this.getFilenameWithExtension(item)
          document.body.appendChild(link)
          link.click()
          document.body.removeChild(link)
          window.URL.revokeObjectURL(url)
        } catch (error) {
          console.error('Error downloading file:', error)
          this.CommonMixin_Snackbar('Fout bij downloaden van export', 'error')
        }
      }
    },
    async openExportDetail (item) {
      if (!this.action) {
        if (item.downloadUrl) {
          try {
            const response = await fetch(item.downloadUrl)
            const blob = await response.blob()
            const url = window.URL.createObjectURL(blob)
            const link = document.createElement('a')
            link.href = url
            link.download = this.getFilenameWithExtension(item)
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            window.URL.revokeObjectURL(url)
          } catch (error) {
            console.error('Error downloading file:', error)
            this.CommonMixin_Snackbar('Fout bij downloaden van export', 'error')
          }
        }
      }
    },
    async DeleteExport () {
      this.deleteLoading = true
      try {
        const response = await templateService.DeleteExport(this.selectedItem.idExport)
        if (response && response.data) {
          this.CommonMixin_Snackbar(`Export '${this.selectedItem.exportNaam}' is succesvol verwijderd`, 'success')
          this.getDataFromApi()
        } else {
          this.CommonMixin_Snackbar(`Fout bij verwijderen van export '${this.selectedItem.exportNaam}'`, 'error')
        }
      } catch (error) {
        this.CommonMixin_Snackbar(`Fout bij verwijderen van export: ${error.message || 'Onbekende fout'}`, 'error')
      } finally {
        this.deleteLoading = false
        this.deletedialog = false
      }
    },
    hasDownloadableExports (items) {
      return items.some(item => item.downloadUrl && item.exportStatusText === 'Export klaar')
    },
    async downloadGroupExport (items) {
      const groupHash = items[0].exportGroepHash
      const groupName = items[0].exportGroepNaam || 'exports'
      this.$set(this.downloadingGroups, groupHash, true)

      try {
        const result = await downloadExportGroupAsZip(items, groupName)

        if (!result.success) {
          this.CommonMixin_Snackbar(result.message, 'error')
          return
        }

        // Show appropriate message based on results
        if (result.failedCount > 0) {
          const failedNames = result.failedItems.map(f => f.item.exportNaam).join(', ')
          this.CommonMixin_Snackbar(
            `${result.downloadedCount} van ${result.downloadedCount + result.failedCount} exports gedownload. Mislukt: ${failedNames}`,
            'warning'
          )
        } else {
          this.CommonMixin_Snackbar(
            `${result.message} als ${result.zipFilename}`,
            'success'
          )
        }
      } catch (error) {
        console.error('Unexpected error in downloadGroupExport:', error)
        this.CommonMixin_Snackbar(`Onverwachte fout: ${error.message}`, 'error')
      } finally {
        this.$set(this.downloadingGroups, groupHash, false)
      }
    }
  }
}
</script>
